"use client";

import { useState } from "react";
import type { CSSProperties } from "react";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";

interface UseDropdownFloatingOptions {
  matchReferenceWidth?: boolean;
  maxHeight?: number;
  minWidth?: number;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function useDropdownFloating({
  matchReferenceWidth = false,
  maxHeight = 240,
  minWidth = 0,
  onOpenChange,
  open,
}: UseDropdownFloatingOptions) {
  const [dropdownWidth, setDropdownWidth] = useState<number>();
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number>();

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange,
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, availableWidth, rects }) {
          const width = matchReferenceWidth
            ? rects.reference.width
            : Math.min(
                Math.max(rects.reference.width, minWidth),
                Math.max(1, availableWidth),
              );
          setDropdownWidth(width);
          setDropdownMaxHeight(Math.min(maxHeight, Math.max(1, availableHeight)));
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  return {
    refs,
    getFloatingProps,
    floatingStyle: {
      ...floatingStyles,
      width: dropdownWidth,
      maxHeight: dropdownMaxHeight,
      zIndex: 9999,
    } satisfies CSSProperties,
  };
}
